import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { recurringCostMatchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

/**
 * Recurring-cost matching is a budget-boxed chunked chain over the
 * candidate-transaction walk (CLAUDE.md section 13,
 * services/recurring_cost_match_chain.py) instead of one synchronous
 * candidate-transaction preload per click -- mirrors
 * AutoMatchChainControl.jsx's shape (chain running, run N, X candidates
 * scanned, Stop control) with no entity scoping, since this feature has
 * always been whole-portfolio.
 */
export function RecurringCostMatchChainControl({ onRunCompleted, testIdPrefix = 'recurring-cost-match-chain' }) {
  const [chain, setChain] = useState(null);
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const fetchChain = useCallback(async () => {
    try {
      const { data } = await recurringCostMatchingAPI.chainStatus();
      setChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const startJobPolling = useCallback((jobId) => {
    stopPolling();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await recurringCostMatchingAPI.jobStatus(jobId);
        if (data.status === 'completed') {
          stopPolling();
          setProgress(null);
          setFailed(null);
          await fetchChain();
          const result = data.result || {};
          const scanned = result.candidate_txn_count || 0;
          if (result.truncated) {
            toast.success(`Recurring-cost matcher run complete -- continuing (${scanned} candidate transaction${scanned !== 1 ? 's' : ''} scanned so far)`);
          } else {
            toast.success(`Recurring-cost matching complete: ${scanned} candidate transaction${scanned !== 1 ? 's' : ''} scanned`);
            if (onRunCompleted) onRunCompleted(result);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          setProgress(data.progress || null);
          setFailed(data.error);
          fetchChain();
          toast.error(data.error || 'Recurring-cost matcher run failed');
        } else if (data.status === 'running') {
          setProgress(data.progress || null);
        }
      } catch (err) {
        stopPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, fetchChain, onRunCompleted]);

  // Recover an in-flight/last-finished job on mount, mirroring
  // AutoMatchChainControl.jsx.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await recurringCostMatchingAPI.jobLatest();
        if (cancelled) return;
        const job = data?.job;
        if (job?.status === 'running') {
          setProgress(job.progress || null);
          startJobPolling(job._id);
        } else if (job?.status === 'failed') {
          setProgress(job.progress || null);
          setFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchChain(); }, [fetchChain]);

  // Keep polling chain status while running, including the gap between one
  // run finishing and its successor starting (no job is "running" to poll
  // in that window).
  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChain]);

  // Once the chain settles on "complete", fire onRunCompleted so the
  // parent re-fetches census/preview/grid -- covers the case where the
  // LAST chunk of a multi-run chain completes between job-status polls
  // (the job itself reports "completed" with truncated=true and stops
  // polling before the chain's own final chunk is seen).
  const chainStatus = chain?.status;
  useEffect(() => {
    if (chainStatus === 'complete' && onRunCompleted) onRunCompleted(chain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainStatus]);

  const runMatcher = async () => {
    setStarting(true);
    setFailed(null);
    try {
      const { data } = await recurringCostMatchingAPI.chainStart();
      if (data.job_id) {
        startJobPolling(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A recurring-cost matcher chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const stopChain = async () => {
    if (!chain?._id) return;
    if (!window.confirm('Stop the running recurring-cost matcher chain? The current run finishes its time window normally.')) return;
    setStopping(true);
    try {
      await recurringCostMatchingAPI.chainStop(chain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStopping(false);
    }
  };

  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortEstimate = chain?.metadata?.narrowed_candidate_estimate;
  const scanned = live?.candidate_txn_count;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={runMatcher} disabled={starting || polling} data-testid={`${testIdPrefix}-run`}>
          {(starting || polling) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(scanned)}${cohortEstimate != null ? `/${fmtInt(cohortEstimate)}` : ''} scanned`
            : 'Run matcher'}
        </Button>
        {running && (
          <Button onClick={stopChain} disabled={stopping} size="sm" variant="outline" data-testid={`${testIdPrefix}-stop`}>
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>
      {chain && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap" data-testid={`${testIdPrefix}-summary`}>
          <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
            {chain.status}
          </Badge>
          <span>
            run {fmtInt(chain.runs_count)}
            {scanned != null && <> -- {fmtInt(scanned)} candidate transaction{scanned !== 1 ? 's' : ''} scanned</>}
          </span>
          {stalled && (
            <span className="text-amber-600 dark:text-amber-400">
              last active {heartbeatAge}s ago -- watchdog will resume it
            </span>
          )}
          {chain.status === 'error' && chain.error && (
            <span className="text-red-600 dark:text-red-400">{chain.error}</span>
          )}
          {failed && <span className="text-red-600 dark:text-red-400">{failed}</span>}
        </div>
      )}
    </div>
  );
}
